/**
 * Every closed set of string values used by the system, declared once.
 *
 * Each set is exported as a `readonly` tuple (so zod can build an enum from it and the
 * server can iterate it) plus the derived union type. The tuples mirror the CHECK
 * constraints in ARCHITECTURE.md §8 exactly — if a constraint changes, change it here
 * and in the migration together.
 */

export const EXECUTION_MODES = ['paper', 'live'] as const;
export type ExecutionMode = (typeof EXECUTION_MODES)[number];

/** ARCHITECTURE.md §5. `starting` is boot-only and never persisted as a resting state. */
export const ENGINE_STATES = ['starting', 'running', 'paused', 'halted', 'panic'] as const;
export type EngineState = (typeof ENGINE_STATES)[number];

export const STRATEGY_KEYS = [
  'sentiment_first',
  'hybrid_confirmation',
  'technical_hunter',
  'trend_follower',
] as const;
export type StrategyKey = (typeof STRATEGY_KEYS)[number];

export const EXIT_KEYS = ['trailing_stop', 'fixed_tpsl', 'scale_out', 'trend_hold'] as const;
export type ExitKey = (typeof EXIT_KEYS)[number];

/**
 * Strategies that manage their own exits get their own exit strategy instead of the default
 * one: a trend trade under a 4% take-profit would be closed long before the trend ends.
 */
export const STRATEGY_EXIT_KEYS: Readonly<Partial<Record<StrategyKey, ExitKey>>> = { trend_follower: 'trend_hold' };

export const EXIT_SCOPES = ['global', 'per_trade'] as const;
export type ExitScope = (typeof EXIT_SCOPES)[number];

export const ORDER_SIDES = ['buy', 'sell'] as const;
export type OrderSide = (typeof ORDER_SIDES)[number];

export const ORDER_TYPES = ['market', 'limit'] as const;
export type OrderType = (typeof ORDER_TYPES)[number];

export const ORDER_PURPOSES = [
  'entry',
  'exit_tp',
  'exit_sl',
  'exit_trail',
  'exit_scaleout',
  'exit_news',
  'panic',
] as const;
export type OrderPurpose = (typeof ORDER_PURPOSES)[number];

/** The `leg` segment of a clientOrderId `et-{tradeId8}-{leg}-{seq}` (AI_RULES §6). */
export const ORDER_LEGS = ['entry', 'tp', 'sl', 'trail', 'scaleout', 'news', 'panic'] as const;
export type OrderLeg = (typeof ORDER_LEGS)[number];

export const ORDER_STATUSES = [
  'pending',
  'submitted',
  'partially_filled',
  'filled',
  'canceled',
  'rejected',
  'failed',
] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const TERMINAL_ORDER_STATUSES = ['filled', 'canceled', 'rejected', 'failed'] as const;

export const TRADE_STATUSES = ['opening', 'open', 'closing', 'closed', 'failed'] as const;
export type TradeStatus = (typeof TRADE_STATUSES)[number];

export const CLOSE_REASONS = [
  'tp',
  'sl',
  'trail',
  'scale_out_final',
  'panic',
  'manual',
  'delisting',
  /** The entry strategy itself said the setup is over (trend follower). */
  'signal_exit',
  /** Part of a position sold to bring it back to its target size (continuous trend). */
  'rebalance',
  /** The exit's `maxHoldMin` ran out before a stop or target was hit. */
  'time',
] as const;
export type CloseReason = (typeof CLOSE_REASONS)[number];

export const SIGNAL_STATUSES = [
  'generated',
  'confirmed',
  'rejected',
  'executed',
  'expired',
] as const;
export type SignalStatus = (typeof SIGNAL_STATUSES)[number];

/** Spot only — 'short' is reserved but rejected by the DB CHECK constraint. */
export const SIGNAL_DIRECTIONS = ['long'] as const;
export type SignalDirection = (typeof SIGNAL_DIRECTIONS)[number];

export const SENTIMENT_DIRECTIONS = ['bullish', 'bearish', 'neutral'] as const;
export type SentimentDirection = (typeof SENTIMENT_DIRECTIONS)[number];

export const SENTIMENT_ADAPTER_KEYS = ['regex', 'claude', 'openai'] as const;
export type SentimentAdapterKey = (typeof SENTIMENT_ADAPTER_KEYS)[number];

/** News sources need no credentials — they read public content only (AI_RULES §1). */
export const CREDENTIAL_PROVIDERS = ['binance', 'claude', 'openai', 'telegram'] as const;
export type CredentialProvider = (typeof CREDENTIAL_PROVIDERS)[number];

/** ARCHITECTURE.md §9.8. */
export const NEWS_SOURCE_KEYS = ['rss', 'binance_announcements'] as const;
export type NewsSourceKey = (typeof NEWS_SOURCE_KEYS)[number];

export const NEWS_CATEGORIES = ['general', 'listing', 'delisting', 'exchange_notice'] as const;
export type NewsCategory = (typeof NEWS_CATEGORIES)[number];

/** What PositionManager does with an open position when its symbol is delisting-vetoed. */
export const DELISTING_ACTIONS = ['close', 'block_only'] as const;
export type DelistingAction = (typeof DELISTING_ACTIONS)[number];

export const VETO_REASONS = ['delisting'] as const;
export type VetoReason = (typeof VETO_REASONS)[number];

/** News-reaction gates (ARCHITECTURE.md §9.7). */
export const GATE_KEYS = ['rvol', 'priced_in', 'btc_regime', 'spread'] as const;
export type GateKey = (typeof GATE_KEYS)[number];

/** Scope of a `config.changed` event (ARCHITECTURE.md §6). */
export const CONFIG_SCOPES = [
  'strategy',
  'exit',
  'risk',
  'universe',
  'sentiment',
  'news',
  'settings',
] as const;
export type ConfigScope = (typeof CONFIG_SCOPES)[number];

export const SNAPSHOT_TYPES = ['utc_midnight', 'periodic', 'event'] as const;
export type SnapshotType = (typeof SNAPSHOT_TYPES)[number];

export const AUDIT_ACTORS = ['user', 'telegram', 'engine', 'system'] as const;
export type AuditActor = (typeof AUDIT_ACTORS)[number];

export const UNIVERSE_MODE_SOURCES = ['scanner', 'whitelist'] as const;
export type UniverseModeSource = (typeof UNIVERSE_MODE_SOURCES)[number];

export const UNIVERSE_LIST_TYPES = ['allowed', 'blacklisted'] as const;
export type UniverseListType = (typeof UNIVERSE_LIST_TYPES)[number];

export const NOTIFICATION_LEVELS = ['info', 'warn', 'critical'] as const;
export type NotificationLevel = (typeof NOTIFICATION_LEVELS)[number];

/** Error envelope codes (API_DOCS.md — Conventions). */
export const ERROR_CODES = [
  'UNAUTHORIZED',
  'FORBIDDEN',
  'VALIDATION_ERROR',
  'NOT_FOUND',
  'CONFLICT',
  'RATE_LIMITED',
  'ENGINE_STATE_INVALID',
  'CONFIRMATION_REQUIRED',
  'PROVIDER_ERROR',
  'INTERNAL',
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

/** Typed confirmation phrases for destructive actions (ARCHITECTURE.md §10). */
export const CONFIRM_PHRASE_GO_LIVE = 'GO-LIVE';
export const CONFIRM_PHRASE_PANIC = 'PANIC';
